import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Award, CheckCircle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function UserDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, ce] = await Promise.all([
          api.get("/me/courses"),
          api.get("/me/certificates"),
        ]);
        setCourses(c.data);
        setCerts(ce.data);
      } catch (e) { setError(errorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const completedSlugs = new Set(certs.map((c) => c.module_slug));
  const nextCourse = courses.find((m) => !completedSlugs.has(m.slug)) || courses[0];

  return (
    <AppShell section="user">
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="overline mb-3">My Learning</div>
        <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tight leading-tight text-foreground mb-2">
          Welcome back, {user?.full_name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-[15px] text-muted-foreground max-w-2xl mb-10">
          Pick up where you left off or dive into the next module of your assigned training.
        </p>

        {/* Resume banner */}
        {nextCourse && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 bg-[#090E17] text-white p-6 lg:p-8 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="overline mb-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                  {certs.length > 0 && courses.length > certs.length ? "Next up" : "Resume learning"}
                </div>
                <div className="font-display font-black text-2xl lg:text-3xl tracking-tight">{nextCourse.name}</div>
                <div className="text-white/60 text-[13px] mt-2">{nextCourse.total_sections} sections · {nextCourse.total_topics} topics</div>
              </div>
              <Link
                data-testid="resume-learning-button"
                to={`/course/${nextCourse.slug}`}
                className="h-12 px-7 inline-flex items-center justify-center bg-white text-[#090E17] font-semibold text-[13px] hover:bg-white/90 whitespace-nowrap"
              >
                Continue training <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}

        {certs.length > 0 && (
          <div className="mb-10 border border-border bg-white p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-primary/10 text-primary inline-flex items-center justify-center"><Award className="h-5 w-5" /></div>
              <div>
                <div className="overline">Achievements</div>
                <div className="font-display font-bold text-[15px] mt-0.5">
                  {certs.length} certificate{certs.length > 1 ? "s" : ""} earned
                </div>
              </div>
            </div>
            <Link data-testid="view-all-certs" to="/certificates" className="h-10 px-4 inline-flex items-center gap-2 border border-border font-semibold text-[12.5px] hover:bg-accent">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-xl tracking-tight">Assigned courses</h2>
          <div className="overline">{courses.length} total</div>
        </div>

        {loading ? (
          <div className="overline">Loading...</div>
        ) : error ? (
          <div className="text-destructive text-[13px]">{error}</div>
        ) : courses.length === 0 ? (
          <div className="border border-border p-8 text-center bg-white" data-testid="no-courses-empty">
            <BookOpen className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="font-display font-bold mt-3">No modules assigned yet</div>
            <p className="text-[13.5px] text-muted-foreground mt-2 max-w-md mx-auto">
              Your administrator hasn't assigned any training tracks yet. You'll receive an email
              as soon as modules are assigned to your account.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border bg-white" data-testid="assigned-courses-grid">
            {courses.map((m, i) => {
              const done = completedSlugs.has(m.slug);
              return (
                <div key={m.slug} className={`p-7 lg:p-8 card-lift ${i === 0 ? "md:border-r" : ""} border-border`}>
                  <div className="flex items-center justify-between mb-5">
                    <span className="sharp-badge">Track {String(i + 1).padStart(2, "0")}</span>
                    {done ? (
                      <span className="sharp-badge text-emerald-700 border-emerald-500/40 bg-emerald-500/10 inline-flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Completed
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">Assigned</span>
                    )}
                  </div>
                  <h3 className="font-display font-black text-xl lg:text-2xl tracking-tight leading-tight mb-3">{m.name}</h3>
                  <p className="text-[13.5px] text-muted-foreground leading-relaxed line-clamp-3">{m.description}</p>
                  <div className="mt-6 flex items-center gap-6 text-[12px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{m.total_sections} sections</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{m.total_topics} topics</span>
                    <span className="inline-flex items-center gap-1.5"><Award className="h-3.5 w-3.5" />Certificate</span>
                  </div>
                  <div className="mt-6 flex gap-2">
                    <Link
                      data-testid={`launch-course-${m.slug}`}
                      to={`/course/${m.slug}`}
                      className="h-10 px-5 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-[#001d6b]"
                    >
                      {done ? "Revisit module" : "Launch module"} <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Link>
                    <a href={m.external_url} target="_blank" rel="noreferrer"
                       className="h-10 px-5 inline-flex items-center border border-border font-semibold text-[13px] hover:bg-accent">
                      Open in new tab
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
